import {createApi, fetchBaseQuery} from "@reduxjs/toolkit/query/react";
import type {RootState} from "../app/store";
import {config} from "../config";
import type {AddEmailRequest, EmailListDetail} from "../types/emailList";
import {EmailList, SearchEmailListsRequest, UpdateEmailList, UpdateEmailStatus} from "../types/emailList";
import {ResourceId} from "../types/resourceId";

export const emailListApi = createApi({
  reducerPath: "emailListApi",
  baseQuery: fetchBaseQuery({
    baseUrl: config.apiBaseUrl,
    prepareHeaders: (headers, {getState}) => {
      const token = (getState() as RootState)?.auth?.accessToken;
      if (token) headers.set("authorization", `Bearer ${token}`);
      return headers;
    },
  }),
  tagTypes: ["emailList", "emailListDetail"] as const,
  endpoints: (builder) => ({

    getEmailListDetail: builder.query<EmailListDetail, SearchEmailListsRequest>({
      query: ({id, page, limit, query, status}) => {
        const searchParams = new URLSearchParams();

        if (page !== undefined) {
          searchParams.set("page", String(page));
        }
        if (limit !== undefined) {
          searchParams.set("limit", String(limit));
        }
        if (query?.trim()) {
          searchParams.set("query", query.trim());
        }

        if (status !== undefined) {
          searchParams.set("status", String(status));
        }

        return {
          url: `/email-list/target-list/${id}`,
          params: searchParams
        }
      },
      providesTags: (_result, _error, {id}) => [
        {type: "emailListDetail", id},
        {type: "emailListDetail", id: "LIST"},
      ],
    }),

    addTargetListEmail: builder.mutation <ResourceId, AddEmailRequest>({
      query: (body) => ({
        url: "/email-list/target-list/add-contacts",
        method: "POST",
        body
      }),
      invalidatesTags: (_result, _error, body) => [
        {type: "emailListDetail", id: body.id},
      ]
    }),

    getEmailLists: builder.query<EmailList[], void>({
      query: (params) => "/email-list/target-lists",
      providesTags: (result) =>
        // is result available?
        result
          ? // successful query
          [
            ...result.map(
              ({id}) => ({type: 'emailList', id}) as const), {type: 'emailList', id: `LIST`},
          ]
          : // an error occurred, but we still want to refetch this query when `{ type: 'Posts', id: 'LIST' }` is invalidated
          [{type: 'emailList', id: 'LIST'}],
    }),

    updateEmailStatus: builder.mutation<void, UpdateEmailStatus>({
      query: (body) => ({
        url: "/email-list/target-list/update-email",
        method: "POST",
        body,
      }),
      invalidatesTags: [{type: "emailListDetail"}],
    }),

    deleteTargetEmail: builder.mutation<void, string>({
      query: (id) => ({
        url: `/email-list/target-list/target-email/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{type: "emailListDetail"}],
    }),

    updateEmailListName: builder.mutation<null, UpdateEmailList>({
      query: (body) => ({
        url: "/email-list/target-list/update-list-detail",
        method: "POST",
        body,
      }),
    }),

    createEmptyEmailList: builder.mutation<ResourceId, { title: string; }>({
      query: ({title}) => {
        const form = new FormData();
        // ⚠️ If your backend expects different field names, change them here:
        form.append("title", title);

        return {
          url: "/email-list/target-list/empty",
          method: "POST",
          body: form,
        };
      },
      invalidatesTags: (result) =>
        result
          ? [{type: "emailList", id: "LIST"}, {type: "emailList", id: result.id}]
          : [{type: "emailList", id: "LIST"}],
    }),

    createEmailList: builder.mutation<ResourceId, { title: string; file: File }>({
      query: ({title, file}) => {
        const form = new FormData();
        // ⚠️ If your backend expects different field names, change them here:
        form.append("title", title);
        form.append("file", file);

        return {
          url: "/email-list/target-list",
          method: "POST",
          body: form,
          // Do NOT set Content-Type manually for multipart:
          // fetch will set the correct boundary for FormData.
        };
      },
      invalidatesTags: (result) =>
        result
          ? [{type: "emailList", id: "LIST"}, {type: "emailList", id: result.id}]
          : [{type: "emailList", id: "LIST"}],
    }),

    deleteTargetEmailList: builder.mutation<null, string>({
      query: (id) => ({
        url: `/email-list/target-list/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [{type: 'emailList', id}],
    }),

    mergeContactsToList: builder.mutation<ResourceId, { emailListId: string; file: File }>({
      query: ({emailListId, file}) => {
        const form = new FormData();
        form.append("id", emailListId);
        form.append("file", file);

        return {
          url: "/email-list/target-list-merge",
          method: "POST",
          body: form,
        };
      },
      invalidatesTags: (result, error, {emailListId}) => [
        {type: "emailListDetail", id: emailListId},
        {type: "emailList", id: "LIST"}
      ],
    }),

    replaceContactsInList: builder.mutation<ResourceId, { emailListId: string; file: File }>({
      query: ({emailListId, file}) => {
        const form = new FormData();
        form.append("id", emailListId);
        form.append("file", file);

        return {
          url: "/email-list/target-list-replace",
          method: "POST",
          body: form,
        };
      },
      invalidatesTags: (result, error, {emailListId}) => [
        {type: "emailListDetail", id: emailListId},
        {type: "emailList", id: "LIST"}
      ],
    }),


  }),
});

export const {
  useGetEmailListDetailQuery,
  useAddTargetListEmailMutation,
  useGetEmailListsQuery,
  useCreateEmailListMutation,
  useCreateEmptyEmailListMutation,
  useDeleteTargetEmailListMutation,
  useUpdateEmailListNameMutation,
  useUpdateEmailStatusMutation,
  useDeleteTargetEmailMutation,
  useMergeContactsToListMutation,
  useReplaceContactsInListMutation,
} = emailListApi;
